import React, { useCallback, useMemo, useState } from 'react';
import { ChartLineUp, Plus } from '@phosphor-icons/react';
import { Button } from 'src/components/Button';
import { PostCallDataExtractionCard } from 'src/components/PostCallDataExtractionCard';
import { PostCallDataExtractionModal } from 'src/components/PostCallDataExtractionModal';
import { Agent, PostCallAnalysisItem } from 'src/interfaces/agent.interface';

type PostCallDataExtractionSectionProps = {
  agent: Agent;
  setAgent: (value: React.SetStateAction<Agent>) => void;
  loading?: boolean;
};

export const PostCallDataExtractionSection: React.FC<
  PostCallDataExtractionSectionProps
> = ({ agent, setAgent, loading = false }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PostCallAnalysisItem | null>(
    null,
  );

  const isDisabled = loading || (!agent.user_id && !!agent.id);

  const items = useMemo(
    () => agent.post_call_analysis_items || [],
    [agent.post_call_analysis_items],
  );

  const handleAdd = useCallback(() => {
    setEditingItem(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((item: PostCallAnalysisItem) => {
    setEditingItem(item);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditingItem(null);
  }, []);

  const handleSave = useCallback(
    (savedItem: PostCallAnalysisItem) => {
      setAgent((prev) => {
        const current = prev.post_call_analysis_items || [];
        const existingIndex = current.findIndex((i) => i.id === savedItem.id);

        const next =
          existingIndex >= 0
            ? current.map((i) => (i.id === savedItem.id ? savedItem : i))
            : [...current, savedItem];

        return {
          ...prev,
          post_call_analysis_items: next,
        };
      });
      handleCloseModal();
    },
    [handleCloseModal, setAgent],
  );

  const handleDelete = useCallback(
    (id: string) => {
      setAgent((prev) => ({
        ...prev,
        post_call_analysis_items: (prev.post_call_analysis_items || []).filter(
          (i) => i.id !== id,
        ),
      }));
      handleCloseModal();
    },
    [handleCloseModal, setAgent],
  );

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex justify-between items-start gap-4">
        <div>
          <h4>Call Insights</h4>
          <p className="text-neutral-400 text-sm">
            Configure which insights to pull from each agent call.
          </p>
        </div>
        <Button onClick={handleAdd} disabled={isDisabled} size="small">
          <Plus className="w-4 h-4 mr-2" />
          Add Call Insight
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-8 text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-3 rounded-full bg-neutral-100">
              <ChartLineUp className="w-5 h-5 text-neutral-400" />
            </div>
          </div>
          <div>
            <h3 className="text-body font-medium text-primary-body mb-1">
              No call insights configured
            </h3>
            <p className="text-description text-neutral-400">
              Automatically capture key insights from every call.
            </p>
          </div>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleAdd}
            disabled={isDisabled}
            size="small"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Call Insight
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <PostCallDataExtractionCard
              key={item.id}
              item={item}
              onClick={() => handleEdit(item)}
              disabled={isDisabled}
            />
          ))}
        </div>
      )}

      <PostCallDataExtractionModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        agent={agent}
        item={editingItem}
        existingItems={items}
        onSave={handleSave}
        onDelete={handleDelete}
        disabled={isDisabled}
      />
    </div>
  );
};
